import type { BoardSummary } from '@doriavboard/shared';
import {
  Copy,
  Download,
  ExternalLink,
  FolderInput,
  FolderOpen,
  LayoutTemplate,
  MoreVertical,
  Pencil,
  Share2,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { errorMessage } from '../api/client';
import { boardsApi } from '../api/endpoints';
import { timeAgo } from '../lib/time';
import { confirmDialog, promptDialog } from '../stores/dialogs';
import { useSession } from '../stores/session';
import { toast } from '../stores/toasts';
import { MoveToSpaceDialog } from './MoveToSpaceDialog';
import { ShareDialog } from './ShareDialog';
import { Avatar } from './ui/Avatar';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from './ui/Menu';
import { Spinner } from './ui/Spinner';

function downloadBoard(board: BoardSummary) {
  const link = document.createElement('a');
  link.href = boardsApi.exportUrl(board.id);
  link.download = '';
  link.click();
}

function BoardCard({
  board,
  showSpace,
  onChanged,
  onRemoved,
  onShare,
  onMove,
}: {
  board: BoardSummary;
  showSpace: boolean;
  onChanged(board: BoardSummary): void;
  onRemoved(id: string): void;
  onShare(board: BoardSummary): void;
  onMove(board: BoardSummary): void;
}) {
  const navigate = useNavigate();
  const me = useSession((s) => s.user);
  const canEdit = board.role !== 'viewer';
  const isOwner = board.role === 'owner';
  const isCreator = board.owner.id === me?.id;

  async function rename() {
    const title = await promptDialog({
      title: 'Rename board',
      initialValue: board.title,
      confirmLabel: 'Rename',
    });
    if (!title || title === board.title) return;
    try {
      const { board: updated } = await boardsApi.update(board.id, { title });
      onChanged(updated);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function duplicate() {
    try {
      const { board: copy } = await boardsApi.duplicate(board.id);
      toast(`Created “${copy.title}”`, 'success');
      navigate(`/b/${copy.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function remove() {
    const ok = await confirmDialog({
      title: `Delete “${board.title}”?`,
      message: 'The board, its frames and images are deleted for everyone. This can’t be undone.',
      confirmLabel: 'Delete board',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await boardsApi.remove(board.id);
      onRemoved(board.id);
      toast('Board deleted');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function leave() {
    if (!me) return;
    const ok = await confirmDialog({
      title: `Leave “${board.title}”?`,
      message: 'You’ll lose access unless the owner shares it with you again.',
      confirmLabel: 'Leave',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await boardsApi.removeMember(board.id, me.id);
      onRemoved(board.id);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:border-slate-300 hover:shadow-float dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700">
      <Link
        to={`/b/${board.id}`}
        className="focus-ring block aspect-[16/10] overflow-hidden rounded-t-xl bg-slate-100 dark:bg-slate-800"
        aria-label={`Open ${board.title}`}
      >
        {board.thumbnailUrl ? (
          <img
            src={board.thumbnailUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition group-hover:scale-[1.02]"
          />
        ) : (
          <div className="dotted-backdrop grid size-full place-items-center text-slate-400">
            <LayoutTemplate className="size-8" />
          </div>
        )}
      </Link>
      {showSpace && board.space && (
        <Link
          to={`/s/${board.space.id}`}
          className="focus-ring absolute top-2 left-2 flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-xs font-medium text-slate-700 shadow-sm backdrop-blur hover:bg-white dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-slate-900"
          title={`In the space “${board.space.name}”`}
        >
          <FolderOpen className="size-3.5 shrink-0" />
          <span className="truncate">{board.space.name}</span>
        </Link>
      )}
      <div className="flex items-start gap-2 p-3">
        <div className="min-w-0 flex-1">
          <Link
            to={`/b/${board.id}`}
            className="block truncate text-sm font-medium hover:underline"
            title={board.title}
          >
            {board.title}
          </Link>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-slate-500">
            {!isCreator && <Avatar name={board.owner.name} color={board.owner.color} size="xs" />}
            {!isCreator && <span className="truncate">{board.owner.name} ·</span>}
            <span className="truncate">Edited {timeAgo(board.updatedAt)}</span>
          </p>
        </div>
        <Menu>
          <MenuTrigger
            className="focus-ring -mr-1 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            aria-label={`Actions for ${board.title}`}
          >
            <MoreVertical className="size-4" />
          </MenuTrigger>
          <MenuContent>
            <MenuItem icon={<ExternalLink />} onSelect={() => navigate(`/b/${board.id}`)}>
              Open
            </MenuItem>
            {canEdit && (
              <MenuItem icon={<Pencil />} onSelect={rename}>
                Rename
              </MenuItem>
            )}
            <MenuItem icon={<Copy />} onSelect={duplicate}>
              Make a copy
            </MenuItem>
            <MenuItem icon={<Share2 />} onSelect={() => onShare(board)}>
              Share
            </MenuItem>
            {isOwner && (
              <MenuItem icon={<FolderInput />} onSelect={() => onMove(board)}>
                Move to space…
              </MenuItem>
            )}
            <MenuItem icon={<Download />} onSelect={() => downloadBoard(board)}>
              Download (.dvb)
            </MenuItem>
            {isOwner && (
              <>
                <MenuSeparator />
                <MenuItem icon={<Trash2 />} tone="danger" onSelect={remove}>
                  Delete
                </MenuItem>
              </>
            )}
            {/* Boards in a space are left by leaving the space. */}
            {!isOwner && !board.space && (
              <>
                <MenuSeparator />
                <MenuItem icon={<Users />} tone="danger" onSelect={leave}>
                  Leave board
                </MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>
      </div>
    </div>
  );
}

type BoardsUpdate = (list: BoardSummary[] | null) => BoardSummary[] | null;

interface BoardGridProps {
  /** Null while loading. */
  boards: BoardSummary[] | null;
  /** What to say when there are no boards. */
  empty: string;
  /** Updates the list the boards came from. */
  onUpdate(update: BoardsUpdate): void;
  /** Label boards with their space (off inside a space, where they all share it). */
  showSpace?: boolean;
  /** Whether a changed board still belongs in this list (say, after it moved out of a space). */
  belongs?(board: BoardSummary): boolean;
}

/** Board cards with their menus, sharing and moving, for the dashboard and spaces. */
export function BoardGrid({ boards, empty, onUpdate, showSpace = true, belongs }: BoardGridProps) {
  const [sharing, setSharing] = useState<BoardSummary | null>(null);
  const [moving, setMoving] = useState<BoardSummary | null>(null);

  function changed(updated: BoardSummary) {
    onUpdate((list) => {
      if (!list) return null;
      if (belongs && !belongs(updated)) return list.filter((b) => b.id !== updated.id);
      return list.map((b) => (b.id === updated.id ? { ...b, ...updated } : b));
    });
  }

  if (boards === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-7 text-slate-400" />
      </div>
    );
  }
  return (
    <>
      {boards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-16 text-center text-sm text-slate-500 dark:border-slate-700">
          {empty}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {boards.map((board) => (
            <BoardCard
              key={board.id}
              board={board}
              showSpace={showSpace}
              onShare={setSharing}
              onMove={setMoving}
              onChanged={changed}
              onRemoved={(id) => onUpdate((list) => list?.filter((b) => b.id !== id) ?? null)}
            />
          ))}
        </div>
      )}

      {sharing && (
        <ShareDialog
          board={sharing}
          open
          onOpenChange={(open) => !open && setSharing(null)}
          onBoardChange={changed}
        />
      )}
      {moving && (
        <MoveToSpaceDialog
          board={moving}
          open
          onOpenChange={(open) => !open && setMoving(null)}
          onMoved={changed}
        />
      )}
    </>
  );
}
